import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  REQUEST_STATUSES,
  SERVICES,
  type RequestDto,
  type RequestStatus,
} from '@shared';
import { apiGet, ApiError } from '../../lib/api';
import { StatusChip } from '../../components/StatusChip';

// Operator pipeline view. Reads the same /api/requests/inbox feed as the inbox
// but lays it out as a column-per-status kanban instead of a flat table, so the
// engagement lead can see at a glance where every brief sits and what's stuck.

const STATUS_LABEL: Record<RequestStatus, string> = {
  submitted: 'Submitted',
  reviewing: 'Reviewing',
  engagement: 'Engagement',
  delivered: 'Delivered',
  closed: 'Closed',
};

const COLUMN_HELP: Record<RequestStatus, string> = {
  submitted: 'Just landed — awaiting first triage.',
  reviewing: 'Lead is shaping scope. Proposal pending.',
  engagement: 'Proposal accepted, delivery in progress.',
  delivered: 'Final deliverable published — awaiting close.',
  closed: 'Engagement wrapped. Invoices settled, no follow-on.',
};

export function AdminPipeline() {
  const [rows, setRows] = useState<RequestDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ requests: RequestDto[] }>('/api/requests/inbox')
      .then((r) => setRows(r.requests))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message ?? 'Failed to load pipeline.'));
  }, []);

  const byStatus = useMemo(() => {
    const groups: Record<RequestStatus, RequestDto[]> = {
      submitted: [], reviewing: [], engagement: [], delivered: [], closed: [],
    };
    for (const r of rows ?? []) groups[r.status].push(r);
    return groups;
  }, [rows]);

  return (
    <>
      <div>
        <h1 className="portal-h1">Pipeline</h1>
        <p className="portal-sub">
          Live view of every brief by engagement state. Click a card to open it in the inbox triage drawer.
        </p>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {rows === null ? (
        <p className="portal-sub" style={{ marginTop: 24 }}>Loading…</p>
      ) : (
        <div className="pipeline-board">
          {REQUEST_STATUSES.map((s) => (
            <section key={s} className="pipeline-col">
              <header className="pipeline-col-h">
                <div>
                  <div className="pipeline-col-title">{STATUS_LABEL[s]}</div>
                  <div className="pipeline-col-sub">{COLUMN_HELP[s]}</div>
                </div>
                <span className="pipeline-count">{byStatus[s].length}</span>
              </header>
              <div className="pipeline-cards">
                {byStatus[s].length === 0 && (
                  <div className="pipeline-empty">Empty column.</div>
                )}
                {byStatus[s].map((r) => {
                  const svc = SERVICES.find((x) => x.id === r.service);
                  return (
                    <Link key={r.id} to={`/admin/requests/${r.id}`} className="pipeline-card card">
                      <div className="pipeline-card-h">
                        <span className="rid">{r.id}</span>
                        <StatusChip status={r.status} />
                      </div>
                      <div className="pipeline-card-title">{r.headline}</div>
                      <div className="pipeline-card-meta">
                        <span>{r.client_name ?? r.client_id}</span>
                        <span className="dot">·</span>
                        <span>{svc?.name ?? r.service}</span>
                      </div>
                      {r.value_pence != null && (
                        <div className="pipeline-card-value">£{(r.value_pence / 100).toLocaleString()}</div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
